'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Portal } from '@common/components';
import { Lottie } from '@common/components/atom/Lottie';
import { useBodyScrollLock } from '@common/hooks';
import { useCallback, useEffect, useState } from 'react';
import CountUp from 'react-countup';
import { Image } from '@/components/client';
import { WELLO_IMAGE_PREFIX_URL } from '@/constants';
export type ModalType = 'win' | 'not-eligible' | 'already-participated';
interface RandomTicketModalProps {
  isOpen: boolean;
  onClose?: () => void;
  type: ModalType;
  point?: number;
  onPrimaryClick?: () => void;
}
const CONFETTI_DELAY_MS = 1000;
const COUNT_UP_DURATION = 1;
const MODAL_BG_COLOR = '#e4302c';
interface ModalConfigItem {
  title: string;
  description: readonly [string, string];
  primaryButtonText: string;
  showTicket: boolean;
  showConfetti: boolean;
}
const MODAL_CONFIG: Record<ModalType, ModalConfigItem> = {
  win: {
    title: '랜덤 웰포인트 당첨!',
    description: ['웰포인트가', '지급되었습니다.'],
    primaryButtonText: '웰포인트 상점 구경하기',
    showTicket: true,
    showConfetti: true
  },
  'not-eligible': {
    title: '기부하고 이벤트 참여하기',
    description: ['오늘의 지역에 기부하신 후', '참여해주세요!'],
    primaryButtonText: '기부하러 가기',
    showTicket: false,
    showConfetti: false
  },
  'already-participated': {
    title: '랜덤 웰포인트 지급 완료!',
    description: ['12월 이벤트에 참여해주셔서', '감사합니다.'],
    primaryButtonText: '친구에게 이벤트 공유하기',
    showTicket: false,
    showConfetti: false
  }
};
const formatNumber = (n: number) => n.toLocaleString('ko-KR');
const getPortalContainer = () => document.body;
const AnimatedPoint = t0 => {
  const $ = _c(2);
  const {
    value
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = <span className="tabular-nums"><CountUp start={0} end={value} duration={COUNT_UP_DURATION} separator="," formattingFn={formatNumber} /></span>;
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export const RandomTicketModal = t0 => {
  const $ = _c(43);
  const {
    isOpen,
    onClose,
    type,
    point: t1,
    onPrimaryClick
  } = t0;
  const point = t1 === undefined ? 0 : t1;
  const [showConfetti, setShowConfetti] = useState(false);
  const config = MODAL_CONFIG[type];
  useBodyScrollLock(isOpen);
  let t2;
  let t3;
  if ($[0] !== isOpen || $[1] !== onClose) {
    t2 = () => {
      if (!isOpen) {
        return;
      }
      const handleKeyDown = e => {
        if (e.key === "Escape") {
          onClose?.();
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    };
    t3 = [isOpen, onClose];
    $[0] = isOpen;
    $[1] = onClose;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] !== config.showConfetti || $[5] !== isOpen) {
    t4 = () => {
      if (!isOpen || !config.showConfetti) {
        return;
      }
      const timer = setTimeout(() => setShowConfetti(true), CONFETTI_DELAY_MS);
      return () => {
        clearTimeout(timer);
        setShowConfetti(false);
      };
    };
    t5 = [isOpen, config.showConfetti];
    $[4] = config.showConfetti;
    $[5] = isOpen;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] !== onClose) {
    t6 = e_0 => {
      e_0.preventDefault();
      e_0.stopPropagation();
      onClose?.();
    };
    $[8] = onClose;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handleBackdropClick = t6;
  if (!isOpen) {
    return null;
  }
  let t7;
  if ($[10] !== handleBackdropClick) {
    t7 = <div className="absolute inset-0 z-[-1] bg-Gray-black/70" onClick={handleBackdropClick} />;
    $[10] = handleBackdropClick;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== showConfetti) {
    t8 = showConfetti && <Lottie className="pointer-events-none absolute inset-0 z-10 size-full" loop={false} src="/assets/jsons/flex-confetti.json" />;
    $[12] = showConfetti;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      backgroundColor: MODAL_BG_COLOR
    };
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== config.title) {
    t10 = <h2 className="text-center font-700 text-Gray-black typo-h5">{config.title}</h2>;
    $[15] = config.title;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== config.description || $[18] !== point || $[19] !== type) {
    t11 = <div className="text-center font-400 text-Gray-white typo-body3">{type === "win" ? <><p><AnimatedPoint value={point} /> 웰포인트가</p><p>지급되었습니다.</p></> : <><p>{config.description[0]}</p><p>{config.description[1]}</p></>}</div>;
    $[17] = config.description;
    $[18] = point;
    $[19] = type;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t10 || $[22] !== t11) {
    t12 = <header className="flex w-full flex-col items-center gap-8">{t10}{t11}</header>;
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== config.showTicket || $[25] !== point) {
    t13 = config.showTicket ? <div className="relative h-136 w-full"><Image src={`${WELLO_IMAGE_PREFIX_URL}/static/event/home-love/market/coupon-modal.png`} alt={"\uB2F9\uCCA8 \uC6F0\uD3EC\uC778\uD2B8"} fill={true} sizes="375px" className="object-contain" /><div className="absolute inset-0 pt-30 flex-center"><p className="font-700 tabular-nums text-Gray-black typo-body1"><AnimatedPoint value={point} /> 웰포인트</p></div></div> : <div className="pointer-events-none relative z-10 -mb-16 h-160 w-240"><Image src={`${WELLO_IMAGE_PREFIX_URL}/static/event/home-love/market/santa-modal.png`} alt={"\uC0B0\uD0C0"} fill={true} sizes="240px" className="object-contain" /></div>;
    $[24] = config.showTicket;
    $[25] = point;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== config.primaryButtonText || $[28] !== onPrimaryClick) {
    t14 = <Button className="w-full" onClick={onPrimaryClick}>{config.primaryButtonText}</Button>;
    $[27] = config.primaryButtonText;
    $[28] = onPrimaryClick;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== onClose) {
    t15 = <button type="button" className="h-48 w-full font-700 text-Gray-white typo-body3" onClick={onClose}>닫기</button>;
    $[30] = onClose;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t14 || $[33] !== t15) {
    t16 = <footer className="flex w-full flex-col items-center gap-2">{t14}{t15}</footer>;
    $[32] = t14;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t12 || $[36] !== t13 || $[37] !== t16) {
    t17 = <article role="dialog" aria-modal="true" className="relative mx-20 flex w-280 flex-col items-center overflow-visible rounded-medium px-20 pb-24 pt-40" style={t9}>{t12}{t13}{t16}</article>;
    $[35] = t12;
    $[36] = t13;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t17 || $[40] !== t7 || $[41] !== t8) {
    t18 = <Portal renderTo={getPortalContainer}><div className="fixed inset-0 z-20 flex-center animate-in fade-in">{t7}{t8}{t17}</div></Portal>;
    $[39] = t17;
    $[40] = t7;
    $[41] = t8;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  return t18;
};